import {
  FloatingPortal,
  type Placement,
  arrow,
  autoPlacement,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import {
  type ElementType,
  type PropsWithChildren,
  type ReactNode,
  useRef,
  useState
} from 'react'
import { themeClass } from '~/styles/theme.css'
import { arrowBase, arrowSide, wrapper } from './Tooltip.css'

type TooltipProps = {
  as?: ElementType
  direction?: Placement
  className?: string
  text?: string | null
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
} & PropsWithChildren

export const Tooltip = ({
  direction,
  text,
  className,
  as: Cmp = 'div',
  onClick,
  children
}: TooltipProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const middleware = [
    offset(10),
    direction ? flip() : autoPlacement(),
    shift({ padding: 5 }),
    arrow({ element: arrowRef })
  ]

  const { x, y, strategy, refs, context, placement, middlewareData } =
    useFloating({
      placement: direction,
      open: isOpen,
      onOpenChange: setIsOpen,
      middleware,
      whileElementsMounted: autoUpdate
    })

  const hover = useHover(context, { move: false })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  const staticSide = placement.split('-')[0] as
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'

  if (!text) {
    return children
  }

  return (
    <>
      <Cmp
        ref={refs.setReference}
        {...getReferenceProps()}
        className={className}
        onClick={onClick}
      >
        {children}
      </Cmp>
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="tooltip"
              ref={refs.setFloating}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              className={clsx(themeClass, wrapper)}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0
              }}
              {...getFloatingProps()}
            >
              {text}
              <div
                ref={arrowRef}
                className={clsx(arrowBase, arrowSide[staticSide])}
                style={{
                  left:
                    middlewareData.arrow?.x != null
                      ? `${middlewareData.arrow.x}px`
                      : '',
                  top:
                    middlewareData.arrow?.y != null
                      ? `${middlewareData.arrow.y}px`
                      : ''
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </>
  )
}
